/**
 * BoardsContext — React context for managing the list of boards with
 * CRUD operations, starring, and cursor-based pagination.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.5
 */

import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BoardSummary {
  readonly boardId: string;
  readonly title: string;
  readonly visibility: 'private' | 'public';
  readonly color: string;
  readonly archived: boolean;
  readonly starred: boolean;
  readonly memberCount: number;
  readonly role?: string;
}

interface PaginationCursor {
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

interface BoardListState {
  readonly boards: BoardSummary[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type BoardsAction =
  | { type: 'FETCH_BOARDS_START' }
  | { type: 'FETCH_BOARDS_SUCCESS'; payload: { boards: BoardSummary[]; pagination: PaginationCursor } }
  | { type: 'FETCH_BOARDS_ERROR'; payload: string }
  | { type: 'FETCH_MORE_START' }
  | { type: 'FETCH_MORE_SUCCESS'; payload: { boards: BoardSummary[]; pagination: PaginationCursor } }
  | { type: 'FETCH_MORE_ERROR'; payload: string }
  | { type: 'STAR_BOARD_OPTIMISTIC'; payload: { boardId: string } }
  | { type: 'UNSTAR_BOARD_OPTIMISTIC'; payload: { boardId: string } }
  | { type: 'STAR_BOARD_REVERT'; payload: { boardId: string } }
  | { type: 'UNSTAR_BOARD_REVERT'; payload: { boardId: string } }
  | { type: 'BOARD_CREATED'; payload: BoardSummary }
  | { type: 'REFETCH' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState: BoardListState = {
  boards: [],
  isLoading: false,
  error: null,
  nextCursor: null,
  hasMore: true,
};

function boardsReducer(state: BoardListState, action: BoardsAction): BoardListState {
  switch (action.type) {
    case 'FETCH_BOARDS_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_BOARDS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        boards: action.payload.boards,
        nextCursor: action.payload.pagination.nextCursor,
        hasMore: action.payload.pagination.hasMore,
        error: null,
      };

    case 'FETCH_BOARDS_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'FETCH_MORE_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_MORE_SUCCESS':
      return {
        ...state,
        isLoading: false,
        boards: [...state.boards, ...action.payload.boards],
        nextCursor: action.payload.pagination.nextCursor,
        hasMore: action.payload.pagination.hasMore,
        error: null,
      };

    case 'FETCH_MORE_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'STAR_BOARD_OPTIMISTIC':
      return {
        ...state,
        boards: state.boards.map((b) =>
          b.boardId === action.payload.boardId ? { ...b, starred: true } : b,
        ),
      };

    case 'UNSTAR_BOARD_OPTIMISTIC':
      return {
        ...state,
        boards: state.boards.map((b) =>
          b.boardId === action.payload.boardId ? { ...b, starred: false } : b,
        ),
      };

    case 'STAR_BOARD_REVERT':
      return {
        ...state,
        boards: state.boards.map((b) =>
          b.boardId === action.payload.boardId ? { ...b, starred: false } : b,
        ),
      };

    case 'UNSTAR_BOARD_REVERT':
      return {
        ...state,
        boards: state.boards.map((b) =>
          b.boardId === action.payload.boardId ? { ...b, starred: true } : b,
        ),
      };

    case 'BOARD_CREATED':
      return { ...state, boards: [action.payload, ...state.boards] };

    case 'REFETCH':
      return { ...initialState, isLoading: true };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface BoardsContextValue {
  readonly state: BoardListState;
  fetchBoards: () => Promise<void>;
  fetchMore: () => Promise<void>;
  starBoard: (boardId: string) => Promise<void>;
  unstarBoard: (boardId: string) => Promise<void>;
  createBoard: (title: string, color?: string) => Promise<void>;
  refetch: () => void;
}

const BoardsContext = createContext<BoardsContextValue | null>(null);

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface BoardsApiResponse {
  readonly data: BoardSummary[];
  readonly pagination: PaginationCursor & { prevCursor: string | null };
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface BoardsProviderProps {
  readonly children: React.ReactNode;
  /** Skip initial fetch — useful for testing */
  readonly skipInitialFetch?: boolean;
}

export function BoardsProvider({ children, skipInitialFetch }: BoardsProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(boardsReducer, initialState);

  const fetchBoards = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_BOARDS_START' });
    try {
      const response = await api.get<BoardsApiResponse>('/v1/boards');
      dispatch({
        type: 'FETCH_BOARDS_SUCCESS',
        payload: {
          boards: response.data,
          pagination: { nextCursor: response.pagination.nextCursor, hasMore: response.pagination.hasMore },
        },
      });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_BOARDS_ERROR', payload: message });
    }
  }, []);

  const fetchMore = useCallback(async (): Promise<void> => {
    if (!state.hasMore || !state.nextCursor || state.isLoading) return;

    dispatch({ type: 'FETCH_MORE_START' });
    try {
      const response = await api.get<BoardsApiResponse>('/v1/boards', { cursor: state.nextCursor });
      dispatch({
        type: 'FETCH_MORE_SUCCESS',
        payload: {
          boards: response.data,
          pagination: { nextCursor: response.pagination.nextCursor, hasMore: response.pagination.hasMore },
        },
      });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_MORE_ERROR', payload: message });
    }
  }, [state.hasMore, state.nextCursor, state.isLoading]);

  const starBoard = useCallback(async (boardId: string): Promise<void> => {
    dispatch({ type: 'STAR_BOARD_OPTIMISTIC', payload: { boardId } });
    try {
      await api.put(`/v1/boards/${boardId}/star`, {});
    } catch {
      dispatch({ type: 'STAR_BOARD_REVERT', payload: { boardId } });
    }
  }, []);

  const unstarBoard = useCallback(async (boardId: string): Promise<void> => {
    dispatch({ type: 'UNSTAR_BOARD_OPTIMISTIC', payload: { boardId } });
    try {
      await api.del(`/v1/boards/${boardId}/star`);
    } catch {
      dispatch({ type: 'UNSTAR_BOARD_REVERT', payload: { boardId } });
    }
  }, []);

  const createBoard = useCallback(async (title: string, color?: string): Promise<void> => {
    try {
      await api.post('/v1/boards', { title, color });
      // Refetch to get the new board in the list
      await fetchBoards();
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_BOARDS_ERROR', payload: message });
    }
  }, [fetchBoards]);

  const refetch = useCallback((): void => {
    dispatch({ type: 'REFETCH' });
  }, []);

  // Fetch boards on mount
  useEffect(() => {
    if (!skipInitialFetch) {
      fetchBoards();
    }
  }, [fetchBoards, skipInitialFetch]);

  // Refetch when REFETCH action resets state
  useEffect(() => {
    if (state.isLoading && state.boards.length === 0 && !state.error && state.nextCursor === null) {
      // Only trigger fetch if REFETCH just reset state
      if (!skipInitialFetch) {
        fetchBoards();
      }
    }
  }, [state.isLoading, state.boards.length, state.error, state.nextCursor, fetchBoards, skipInitialFetch]);

  const value: BoardsContextValue = {
    state,
    fetchBoards,
    fetchMore,
    starBoard,
    unstarBoard,
    createBoard,
    refetch,
  };

  return <BoardsContext.Provider value={value}>{children}</BoardsContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the boards context. Must be used within a BoardsProvider.
 */
export function useBoards(): BoardsContextValue {
  const context = useContext(BoardsContext);
  if (!context) {
    throw new Error('useBoards must be used within a BoardsProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (api.isClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Export reducer and actions for testing
export { boardsReducer, initialState };
export type { BoardListState, BoardsAction };
